import * as React from 'react';
import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { Alert, Checkbox, Col, Form, Input, Row, Select, Typography, message } from 'antd';
import { Contract, Tree, User } from 'tree-wallet-client';
import { TWPositionData, watchHistory, hasSufficientAccuracy, isValidCoordinates, watchPosition, calculateDistance, calculateGpsAccuracyState } from '../../../Service/GeolocationService';
import { EventedArrayEvent } from '../../../Dependency/EventedArray';
import TreeImageUpload from '../../FormPage/TreeImageUpload';
import GeolocationData from '../../FormPage/GeolocationData';
import { addValidatedTreeAsync } from '../../../Service/TreeService';
import { getFeatureFlag } from '../../../Service/FeatureFlags';
import { DEBUG_GPS_WARNING, TREE_QUEUED, TREE_QUEUE_FAILED, WAITING_FOR_GPS } from '../../../Helper/BannerMessages';

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface MapPopupValidationFormProps {
    tree: Tree;
    user: User;
    contract: Contract;
    onCancel: () => void;
    onSuccess: () => void;
    language: string;
    onPhotoCapture: (url?: string) => void;
    onValidationStart?: () => void;
    /** The map page's own state updater, handed over because the popup root has no context */
    updateState: Function;
}

const questions = [
    { key: 'treeStatus', label: 'Tree status', options: ['Alive', 'Dead', 'Not sure'] },
    { key: 'leavesFallOff', label: 'Leaves fall off?', options: ['Yes', 'No'] },
    { key: 'treeCutDown', label: 'Tree cut down?', options: ['Yes', 'No'] },
    { key: 'gotEaten', label: 'Got eaten?', options: ['Yes', 'No'] },
    { key: 'gotDugUp', label: 'Got dug up?', options: ['Yes', 'No'] },
    { key: 'gotBurned', label: 'Got burned?', options: ['Yes', 'No'] },
    { key: 'leavesResprout', label: 'Leaves resprout?', options: ['Yes', 'No'] },
];

// Maximum distance (in meters) allowed between the validator and the tree to take
// the photo, independent from MINIMUM_ACCURACY threshold on target server.
const MAX_DISTANCE_TO_TREE = 10;

interface CommentTextAreaProps {
    value?: string;
    onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
    [key: string]: any;
}

// Comment field of the validation form. Its vertical scroll position is drawn on
// the right edge (same approach as the select options in `TreeTypeSelect`): iOS
// and Android hide their overlay scrollbars and iOS dropped `::-webkit-scrollbar`
// in iOS 14, so nothing would show the position when the comment is longer than
// the 5 rows.
const CommentTextArea = React.forwardRef<any, CommentTextAreaProps>(function CommentTextArea(props: CommentTextAreaProps, ref: any) {
    const { value, onChange, ...rest } = props;
    const wrapperRef = React.useRef<HTMLDivElement>(null);
    const [thumb, setThumb] = React.useState<{ top: number; height: number } | null>(null);

    const updateThumb = React.useCallback(() => {
        const field = wrapperRef.current?.querySelector('textarea');
        let next: { top: number; height: number } | null = null;

        if (field) {
            const maxScroll = field.scrollHeight - field.clientHeight;

            if (maxScroll > 0) {
                const height = Math.min(
                    field.clientHeight,
                    Math.max(20, (field.clientHeight / field.scrollHeight) * field.clientHeight)
                );
                // iOS rubber-bands past both ends: scrollTop goes negative at the
                // top and beyond maxScroll at the bottom, which used to push the
                // thumb outside the field, so it is clamped to the scrollable range.
                const scrollTop = Math.min(Math.max(field.scrollTop, 0), maxScroll);
                next = { top: (scrollTop / maxScroll) * (field.clientHeight - height), height };
            }
        }

        // Keep the previous object when nothing moved, otherwise the layout effect
        // below would re-render on every render.
        setThumb((previous: { top: number; height: number } | null) => (
            previous && next && previous.top === next.top && previous.height === next.height
                ? previous
                : next
        ));
    }, []);

    // The text height changes while typing, so it is recomputed after each render.
    React.useLayoutEffect(updateThumb);

    return (
        <div className={'comment-textarea-wrapper'} ref={wrapperRef}>
            <TextArea
                rows={5}
                placeholder="Write your comment here"
                ref={ref}
                value={value}
                onChange={onChange}
                onScroll={updateThumb}
                {...rest}
            />
            {thumb &&
                <div className={'comment-textarea-thumb'} style={{ top: thumb.top, height: thumb.height }} />
            }
        </div>
    );
});

export default function MapPopupValidationForm(props: MapPopupValidationFormProps) {
    const { tree, onSuccess, onPhotoCapture, language, onValidationStart, updateState } = props;
    const [form] = Form.useForm();
    const [displayCoordinates, setDisplayCoordinates] = useState<TWPositionData | undefined>(undefined);
    const [coordinates, setCoordinates] = useState<TWPositionData | undefined>(undefined);
    const [showComment, setShowComment] = useState<boolean>(false);
    const [, setLoading] = useState<boolean>(false);
    const [unansweredQuestions, setUnansweredQuestions] = useState<string[]>([]);
    const [validationAttempted, setValidationAttempted] = useState<boolean>(false);
    const [bannerMessage, setBannerMessage] = useState<string | undefined>(undefined);
    const [bannerType, setBannerType] = useState<'success' | 'info' | 'warning' | 'error' | undefined>(undefined);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const hasImageRef = React.useRef<boolean>(false);
    // Set when the "Click to upload" press passes the checks and the camera opens:
    // from then on only a better position replaces the stored one, which is the
    // position sent in the payload.
    const keepBestPositionRef = React.useRef<boolean>(false);
    // Whether the user was close enough the last time the banner was updated, or
    // null while no distance banner has to be followed (see the effect below).
    const isCloseEnoughRef = React.useRef<boolean | null>(null);

    const checkAllFields = (changedValues?: any, allValues?: any) => {
        const values = allValues || form.getFieldsValue();
        const unanswered = questions.filter(q => values[q.key] === undefined || values[q.key] === null).map(q => q.key);

        if (validationAttempted) {
            setUnansweredQuestions(unanswered);

            // Nothing is unanswered anymore: the "please answer all the
            // questions" banner is dropped - both here and in the page banner it
            // was mirrored into (see validateQuestionsBeforePhoto). Any other
            // message (GPS accuracy, distance to the tree) is left alone.
            if (unanswered.length === 0 && bannerMessage === 'Please answer all the questions') {
                setBannerMessage(undefined);
                if (updateState) {
                    updateState({ showBanner: false });
                }
            }
        }
    };

    const validateQuestionsBeforePhoto = () => {
        setValidationAttempted(true);
        const values = form.getFieldsValue();
        const unanswered = questions.filter(q => values[q.key] === undefined || values[q.key] === null).map(q => q.key);

        if (unanswered.length > 0) {
            setUnansweredQuestions(unanswered);
            setBannerMessage('Please answer all the questions');
            setBannerType('error');
            if (updateState) {
                updateState({
                    showBanner: true,
                    bannerMessage: 'Please answer all the questions',
                    bannerType: 'error'
                });
            }
            // Trigger antd validation to show red borders on select boxes
            form.validateFields(unanswered).catch(() => { });
            return false;
        }
        setBannerMessage(undefined);
        return true;
    };

    const debugMode = getFeatureFlag('debug');
    const gpsReady = debugMode || hasSufficientAccuracy(displayCoordinates?.accuracy);

    // Puts the cursor (and the mobile keyboard) in the comment field as soon as
    // "Optional comment" is ticked, and takes it out again when it is unticked.
    const handleCommentToggle = (checked: boolean) => {
        if (!checked) {
            const commentField = containerRef.current?.querySelector('textarea');
            // Removing the field from the DOM usually drops the focus with it,
            // but blurring it explicitly is what actually closes the keyboard.
            if (commentField && document.activeElement === commentField) {
                commentField.blur();
            }
            setShowComment(false);
            return;
        }

        // The textarea is mounted by this state change, so it is rendered
        // synchronously to keep the focus() call inside the checkbox tap: iOS
        // only opens the keyboard for a focus() made during the user gesture
        // itself, a useEffect would run too late.
        flushSync(() => setShowComment(true));

        // The comment field is the only textarea of this form, and it exists only
        // from the render above.
        const commentField = containerRef.current?.querySelector('textarea');
        if (!commentField) {
            return;
        }

        commentField.focus();

        // The text is kept when the field is unticked, so the cursor goes after
        // it rather than at the beginning, and the field is scrolled down when
        // the comment is longer than the 5 rows.
        const textEnd = commentField.value.length;
        commentField.setSelectionRange(textEnd, textEnd);
        commentField.scrollTop = commentField.scrollHeight;
    };

    useEffect(() => {
        const handleCoordUpdate = (newCoords: TWPositionData) => {
            setCoordinates(currentCoordinates => {
                const newState = calculateGpsAccuracyState(currentCoordinates, newCoords, keepBestPositionRef.current || hasImageRef.current);

                if ('displayCoordinates' in newState) {
                    setDisplayCoordinates(newState.displayCoordinates);
                }

                if ('coordinates' in newState) {
                    return newState.coordinates;
                }
                return currentCoordinates;
            });
        };

        const lastCoords = watchHistory.last();
        if (lastCoords && !lastCoords.error && lastCoords.lat && lastCoords.lng) {
            handleCoordUpdate(lastCoords);
        }

        watchPosition(undefined, (error: any) => {
            console.error('GPS Error callback:', error);
        });

        watchHistory.on(EventedArrayEvent.add, handleCoordUpdate);

        const scrollTimeout = setTimeout(() => {
            if (containerRef.current) {
                containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 300);

        const handleScroll = (event: any) => {
            if (event.target && event.target.closest && event.target.closest('.ant-select-dropdown')) {
                return;
            }

            if (document.activeElement instanceof HTMLInputElement && document.activeElement.closest('.ant-select')) {
                return;
            }

            // Never blur a textarea: the comment field is focused when "Optional
            // comment" is ticked, and opening the keyboard scrolls the page -
            // blurring it here would close the keyboard straight away.
            if (document.activeElement instanceof HTMLTextAreaElement) {
                return;
            }

            if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }
            const openSelects = document.querySelectorAll('.ant-select-open');
            openSelects.forEach((el) => {
                if (el instanceof HTMLElement) el.blur();
            });
        };

        window.addEventListener('scroll', handleScroll, true);

        return () => {
            clearTimeout(scrollTimeout);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, []);

    // Once the user has been asked to get closer to the tree, the banner follows
    // the user's distance from the tree: green as soon as the distance allows the photo, 
    // red again if the user walks away
    useEffect(() => {
        const wasCloseEnough = isCloseEnoughRef.current;

        // Skip if user hasn't been asked to get closer to the tree yet
        if (wasCloseEnough === null) {
            return;
        }
        // Stop banner updates if the live position or the tree position is missing,
        // or the fix is not accurate enough. `displayCoordinates` is used, not
        // `coordinates`: the latter keeps the last position with a good accuracy
        // (see calculateGpsAccuracyState), so it never looks degraded.
        else if (
            !displayCoordinates || !coordinates || !tree.lat || !tree.lng
            || typeof coordinates.lat !== 'number' || typeof coordinates.lng !== 'number'
            || !isValidCoordinates(displayCoordinates)
            || !hasSufficientAccuracy(displayCoordinates.accuracy)
        ) {
            setBannerMessage(undefined);
            if (updateState) {
                updateState({ showBanner: false });
            }
            return;
        }

        // Check if close enough & sufficient accuracy, and update the banner if the state changed since last time
        const distance = calculateDistance(coordinates.lat, coordinates.lng, tree.lat, tree.lng);
        const isCloseEnough = distance <= MAX_DISTANCE_TO_TREE;

        if (isCloseEnough === wasCloseEnough) {
            return;
        }

        isCloseEnoughRef.current = isCloseEnough;

        const message = isCloseEnough ? 'Now you can take the photo' : 'Please get closer to the tree';
        const type = isCloseEnough ? 'success' : 'error';

        setBannerMessage(message);
        setBannerType(type);
        if (updateState) {
            updateState({ showBanner: true, bannerMessage: message, bannerType: type });
        }
    }, [displayCoordinates, coordinates, tree.lat, tree.lng, updateState]);

    const onFinish = async (values: any) => {
        const debugMode = getFeatureFlag('debug');

        if (!debugMode && (!coordinates || !hasSufficientAccuracy(coordinates.accuracy))) {
            message.error(WAITING_FOR_GPS);
            return;
        }

        if (debugMode && (!coordinates || !hasSufficientAccuracy(coordinates.accuracy))) {
            message.warning(DEBUG_GPS_WARNING);
        }

        setLoading(true);
        onValidationStart?.(); // Signal that validation has started

        try {
            // Filter out optionalCommentEnabled from the values
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { optionalCommentEnabled: _optionalCommentEnabled, ...filteredValues } = values;

            const body = {
                ...filteredValues,
                lat: coordinates?.lat,
                lng: coordinates?.lng,
                accuracy: coordinates?.accuracy,
                altitude: (coordinates?.altitude !== undefined && coordinates?.altitude !== null) ? coordinates.altitude : 0,
                altitude_accuracy: coordinates?.altitudeAccuracy,
                // orientation: coordinates?.orientation,
                speed: coordinates?.speed,
            };

            const photo = form.getFieldValue('photo');
            if (photo) {
                body.photo = photo;
            }

            const translation = tree.treeType.translations[language]
                ? tree.treeType.translations[language]
                : tree.treeType.translations.latin;

            await addValidatedTreeAsync({
                id: String(tree.id),
                type: translation,
                number: String(tree.numberPlanted || 1),
                validationData: body,
            });

            message.success(TREE_QUEUED);

            if (updateState) {
                updateState({ showBanner: true });
            }

            onSuccess();
        } catch (err) {
            console.error(err);
            message.error(TREE_QUEUE_FAILED);
        } finally {
            // Whatever the outcome, the queue button goes back to its count.
            if (updateState) {
                updateState({ queueSaving: false });
            }
            setLoading(false);
        }
    };

    return (
        <div
            ref={containerRef}
            style={{ marginTop: '20px', borderTop: '1px solid #f0f0f0', paddingTop: '20px', width: '100%' }}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                onFinishFailed={() => {
                    // The submit never reached the queue (a question was left unanswered while the
                    // photo was being taken): the spinner must not stay on the queue button.
                    if (updateState) {
                        updateState({ queueSaving: false });
                    }
                }}
                onValuesChange={() => checkAllFields()}
                initialValues={{ optionalCommentEnabled: false }}
                style={{ width: '100%' }}
            >

                <Row gutter={[16, 0]}>
                    {questions.map((q) => (
                        <Col xs={24} sm={12} key={q.key}>
                            <Form.Item
                                name={q.key}
                                label={<Text strong>{q.label}</Text>}
                                rules={[{ required: true, message: 'Please set an answer!' }]}
                                validateStatus={unansweredQuestions.includes(q.key) ? 'error' : undefined}
                            >
                                <Select
                                    placeholder="Select answer"
                                    style={{
                                        width: '100%',
                                    }}
                                    getPopupContainer={(triggerNode) => triggerNode.parentElement}
                                >
                                    {q.options.map((opt) => (
                                        <Option key={opt} value={opt}>{opt}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    ))}
                </Row>

                <Form.Item name="optionalCommentEnabled" valuePropName="checked" style={{ marginBottom: 8 }}>
                    <Checkbox onChange={(e) => handleCommentToggle(e.target.checked)}>
                        Optional comment
                    </Checkbox>
                </Form.Item>

                {showComment && (
                    <Form.Item name="comment">
                        <CommentTextArea />
                    </Form.Item>
                )}

                <GeolocationData coordinates={displayCoordinates} />

                <Form.Item
                    name="photo"
                    rules={[{ required: true, message: 'Please take a photo of the tree!' }]}
                >
                    <TreeImageUpload
                        enabled={gpsReady}
                        name='photo'
                        // The kept position, i.e. the best accuracy since the camera was opened:
                        // the one this validation is queued with (see calculateGpsAccuracyState).
                        gpsAccuracy={coordinates?.accuracy ?? null}
                        formRef={{ current: form } as any}
                        formSetState={(data: any) => {
                            if (data.hasImage !== undefined) {
                                hasImageRef.current = data.hasImage;
                            }
                            if (data.previewImage !== undefined) onPhotoCapture(data.previewImage);
                            checkAllFields();

                            // Directly save and close once photo is confirmed
                            if (data.confirmed) {
                                // The tree is on its way to the validator's queue: the queue button
                                // shows a spinner in place of its count until the write has answered
                                // (cleared below, whatever the outcome).
                                if (updateState) {
                                    updateState({ queueSaving: true });
                                }
                                form.submit();
                            }
                        }}
                        onClick={() => {
                            // 1. Check missing questions
                            if (!validateQuestionsBeforePhoto()) {
                                return false;
                            }

                            // 2. Check distance between validator and tree
                            const treeLat = tree.lat;
                            const treeLng = tree.lng;
                            const validatorLat = coordinates?.lat;
                            const validatorLng = coordinates?.lng;

                            if (treeLat && treeLng && validatorLat && validatorLng) {
                                const distance = calculateDistance(validatorLat, validatorLng, treeLat, treeLng);
                                if (distance > MAX_DISTANCE_TO_TREE) {
                                    // Hands the banner over to the effect above: it
                                    // turns green as soon as the user is close enough,
                                    // and red again if the user walks away.
                                    isCloseEnoughRef.current = false;
                                    setBannerMessage('Please get closer to the tree');
                                    setBannerType('error');
                                    return false;
                                }
                                // Pressing "Take Photo" opens the camera during
                                // which the stored position only improves, whether or not
                                // a photo is taken afterwards
                                keepBestPositionRef.current = true;
                            }

                            return true;
                        }}
                    />
                </Form.Item>
            </Form>
            {bannerMessage && (
                <Alert
                    message={bannerMessage}
                    type={bannerType}
                    showIcon
                    style={{ marginBottom: '16px' }}
                />
            )}
        </div>
    );
}
